import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api, iso, monthGrid } from "@/lib/api";
import { nextSelection, rangeIsFree } from "@/lib/calendar";
import { useI18n } from "@/i18n";

const DayCell = ({ cell, info, unavailable, checkoutOnly, isStart, isEnd, inRange, onClick }) => {
  const dIso = iso(cell);
  return (
    <button
      type="button"
      disabled={unavailable}
      data-testid={`cal-day-${dIso}`}
      onClick={() => onClick(dIso)}
      className={[
        "flex h-14 flex-col items-center justify-center text-sm",
        unavailable ? "cursor-not-allowed text-muted-foreground/40 line-through" : "hover:bg-white/[0.06]",
        checkoutOnly && !isEnd ? "text-bone/70" : "",
        isStart || isEnd ? "!bg-brass !text-ink" : "",
        inRange ? "bg-white/10" : "",
      ].join(" ")}
      style={{ transition: "background-color .18s ease" }}
    >
      <span className="font-medium">{cell.getDate()}</span>
      {!unavailable && checkoutOnly && (
        <span className={`text-[9px] uppercase tracking-wide ${isEnd ? "text-ink/80" : "text-brass/80"}`}>↦</span>
      )}
      {!unavailable && !checkoutOnly && info && (
        <span className={`text-[10px] ${isStart || isEnd ? "text-ink/80" : "text-brass"}`}>
          {Math.round(info.price)}€
        </span>
      )}
    </button>
  );
};

const MonthView = ({ month, cells, days, range, todayIso, labels, onDayClick }) => (
  <div>
    <p className="mb-3 text-center font-display text-lg capitalize text-bone">
      {labels.months[month.getMonth()]} {month.getFullYear()}
    </p>
    <div className="mb-1 grid grid-cols-7 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
      {labels.dow.map((x, i) => (
        <span key={`dow-${i}`}>{x}</span>
      ))}
    </div>
    <div className="grid grid-cols-7 gap-1">
      {cells.map((cell, i) => {
        if (!cell) return <span key={`pad-${month.getFullYear()}-${month.getMonth()}-${i}`} />;
        const dIso = iso(cell);
        const info = days[dIso];
        const past = dIso < todayIso;
        // Le jour de départ ne consomme pas de nuit : il reste cliquable même si cette nuit est vendue.
        const canBeEnd =
          !!range?.start && !range?.end && dIso > range.start && !past && rangeIsFree(days, range.start, dIso);
        return (
          <DayCell
            key={dIso}
            cell={cell}
            info={info}
            checkoutOnly={canBeEnd && (!info || !info.available)}
            unavailable={past || ((!info || !info.available) && !canBeEnd)}
            isStart={range?.start === dIso}
            isEnd={range?.end === dIso}
            inRange={!!range?.start && !!range?.end && dIso > range.start && dIso < range.end}
            onClick={onDayClick}
          />
        );
      })}
    </div>
  </div>
);

/**
 * Calendrier de disponibilité avec prix par nuit.
 * onSelect({start, end}) — dates ISO ou null.
 */
export const AvailabilityCalendar = ({ giteSlug, range, onSelect, months = 2 }) => {
  const { t } = useI18n();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);

  const start = iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const end = iso(new Date(cursor.getFullYear(), cursor.getMonth() + months + 1, 1));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/availability", { params: { gite_slug: giteSlug, start, end } })
      .then((r) => {
        if (!alive) return;
        const map = {};
        r.data.days.forEach((d) => (map[d.date] = d));
        setDays(map);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [giteSlug, start, end]);

  const grids = useMemo(
    () =>
      Array.from({ length: months }, (_, i) => {
        const d = new Date(cursor.getFullYear(), cursor.getMonth() + i, 1);
        return { d, cells: monthGrid(d.getFullYear(), d.getMonth()) };
      }),
    [cursor, months]
  );

  const labels = { months: t("months"), dow: t("dow") };

  return (
    <div data-testid="availability-calendar" className="relative">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          data-testid="cal-prev"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="grid h-10 w-10 place-items-center border border-white/15 text-bone hover:bg-white/5"
        >
          <ChevronLeft size={18} />
        </button>
        {loading && (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> {t("cal.loading")}
          </span>
        )}
        <button
          type="button"
          data-testid="cal-next"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="grid h-10 w-10 place-items-center border border-white/15 text-bone hover:bg-white/5"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className={`grid gap-8 ${months > 1 ? "md:grid-cols-2" : ""}`}>
        {grids.map(({ d, cells }) => (
          <MonthView
            key={`${d.getFullYear()}-${d.getMonth()}`}
            month={d}
            cells={cells}
            days={days}
            range={range}
            todayIso={iso(today)}
            labels={labels}
            onDayClick={(dIso) => onSelect(nextSelection(range, dIso, days))}
          />
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-2"><span className="h-3 w-3 bg-brass" /> {t("cal.selection")}</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 bg-white/10" /> {t("cal.included")}</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 bg-muted line-through" /> {t("cal.unavailable")}</span>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
